import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { forwardRef, useImperativeHandle } from "react";
import { Control, FieldArrayWithId, useFieldArray, useForm, UseFormSetError } from "react-hook-form";
import FormInputText from "../components/Form/FormInputText";
import { BaseQuestionOption, Question, QuestionOption, QuestionTypeEnum } from "../models/question.model";
import FormSelection from "../components/Form/FormSelection";
import {
  enumToArraySelection,
  onHandleFormSubmit,
  isDisableFutureQuestion,
  isFilterQuestion,
  isTextInputQuestion,
  titleStr,
} from "../lib/helper";
import { AddCircleOutline, ArrowDownward, Close } from "@mui/icons-material";
import UploadCSVButton from "../components/UploadCSVButton";
import VirtualComponent from "../components/VirtualComponent";
import { AddTranslationButton, KeyLabel } from "./AddTranslation";
import Tooltip from "@mui/material/Tooltip";

export const isSelectionType = (type: QuestionTypeEnum) => {
  return [
    QuestionTypeEnum.dropdown,
    QuestionTypeEnum.singleHorizontalRadio,
    QuestionTypeEnum.singleVerticalRadio,
    QuestionTypeEnum.multipleCheckbox,
    QuestionTypeEnum.multipleDropdown,
    QuestionTypeEnum.multipleRowColumnRadio,
  ].includes(type);
};

export const hasSubOptionsType = (type: QuestionTypeEnum) => {
  return [QuestionTypeEnum.multipleDropdown, QuestionTypeEnum.multipleRowColumnRadio].includes(type);
};

const selection_type_label: { [key in QuestionTypeEnum]?: string } = {
  [QuestionTypeEnum.multipleDropdown]: "Dropdown",
  [QuestionTypeEnum.multipleRowColumnRadio]: "Radio Row",
};

const helper_text: { [key in QuestionTypeEnum]?: string } = {
  [QuestionTypeEnum.imageVideo]: "Key must include image_ as prefix",
};

const getOptionsKeyLabel = (options: QuestionOption[]): KeyLabel[] => {
  const getKeyLabel = (option: QuestionOption): KeyLabel[] => {
    //Filter out fields that do not contain text
    const keyLabel = /^[^a-zA-Z]*$/.test(option.label)
      ? []
      : [
          {
            key: option.key,
            label: option.label,
          },
        ];
    if (!option.subOptions) {
      return keyLabel;
    }
    return [...keyLabel, ...option.subOptions.flatMap((subOption) => getKeyLabel(subOption))];
  };

  return options.flatMap((option) => getKeyLabel(option));
};

const validateKeyMessageMap: {
  [key in QuestionTypeEnum]?: { message: string; regex: RegExp };
} = {
  [QuestionTypeEnum.imageVideo]: {
    message: "Key must include image_ as prefix",
    regex: /^image_[\w-]+$/,
  },
};
export default forwardRef(
  (
    props: {
      defaultValues?: Partial<Question>;
      isModal?: boolean;
    },
    ref: any
  ) => {
    const { isModal, defaultValues } = props;
    const methods = useForm<Question & { radioRows?: QuestionOption[] }>({
      defaultValues: defaultValues
        ? {
            ...defaultValues,
            radioRows:
              defaultValues.type === QuestionTypeEnum.multipleRowColumnRadio
                ? defaultValues.options?.[0]?.subOptions?.map((r) => ({
                    key: r.key.slice(r.key.indexOf("_rr_") + 4),
                    label: r.label,
                  }))
                : [{ label: "", key: "" }],
          }
        : {
            options: [{ label: "", key: "" }],
            radioRows: [{ label: "", key: "" }],
          },
    });
    const { handleSubmit, control, setError, watch, getValues, setValue } = methods;
    const type = watch("type");
    useImperativeHandle(ref, () => ({
      onSubmit() {
        return onHandleFormSubmit(handleSubmit, async (v) => {
          const radioRows = v.radioRows;
          delete v.radioRows;
          if (type === QuestionTypeEnum.multipleRowColumnRadio) {
            for (const option of v.options) {
              option.subOptions = (radioRows || []).map((r) => ({
                key: `${option.key}_rr_${r.key}`,
                label: r.label,
              }));
            }
          }
          if (!hasSubOptionsType(type)) {
            for (let option of v.options) {
              if (option.subOptions) {
                delete option.subOptions;
              }
            }
          }
          return v;
        });
      },
    }));

    const { fields, append, remove, move, update } = useFieldArray({
      control,
      name: "options",
      rules: {
        required: "Please add some content",
        validate: (val) => {
          if (!isSelectionType(type) && val.length > 1) {
            return `For ${titleStr(type)} of question, you only need one option.`;
          }
          return true;
        },
      },
    });

    const updateSubOptions = (optionIndex: number, subOptions: QuestionOption[]) => {
      update(optionIndex, {
        ...getValues(`options.${optionIndex}`),
        subOptions: subOptions,
      });
    };

    const MultipleDropdownOption = (props: { option: FieldArrayWithId<Question, "options", "id">; i: number }) => {
      const { option, i } = props;

      const SubRow = ({ index: j }: { index: number }) => {
        const sub = option.subOptions?.[j];
        if (!sub) {
          return <></>;
        }
        return (
          <div style={{ height: 280, overflow: "hidden" }}>
            {
              <Grid item xs={12} key={`${sub.key}${j}`}>
                <Accordion defaultExpanded>
                  <AccordionSummary>
                    <Stack direction={"row"} justifyContent={"space-between"}>
                      <Stack justifyContent={"center"}>Sub option {j + 1}</Stack>
                      <IconButton
                        color={"error"}
                        size={"small"}
                        onClick={() =>
                          updateSubOptions(
                            i,
                            (option.subOptions || []).filter((_, k) => k !== j)
                          )
                        }
                      >
                        <Close />
                      </IconButton>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormInputText
                          name={`options.${i}.subOptions.${j}.key`}
                          control={control}
                          setError={setError}
                          label="Key"
                          required
                          rules={{
                            required: "Key is required",
                            pattern: {
                              value: type === QuestionTypeEnum.imageVideo ? /^image_[\w-]+$/ : /^[\w-]+$/,
                              message: "Key can not use any special character or space",
                            },
                          }}
                          helperText={helper_text[type]}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormInputText
                          name={`options.${i}.subOptions.${j}.label`}
                          control={control}
                          setError={setError}
                          label="Label"
                          multiline
                          rows={3}
                          required
                          rules={{ required: "Label is required" }}
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            }
          </div>
        );
      };
      return (
        <>
          <Grid item container xs={12} spacing={2}>
            <Grid item xs={12}>
              <Stack sx={{ py: 2, width: "100%" }} direction="row" alignItems="flex-start" justifyContent="space-between">
                <div style={{ width: 200 }}>
                  <UploadCSVButton<BaseQuestionOption>
                    keys={["key", "label"]}
                    label="Overwrite by csv"
                    key={type}
                    onChange={(data) => {
                      updateSubOptions(i, data);
                    }}
                    sample={`key,label\nsample_key,sample_label`}
                  />
                </div>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    updateSubOptions(i, [{ key: "", label: "" }]);
                  }}
                >
                  Clear All Sub Option
                </Button>
              </Stack>
            </Grid>
            {
              <VirtualComponent
                key={`sub_${i}_${(option.subOptions || []).length}`}
                itemHeight={280}
                count={(option.subOptions || []).length}
                height={280 * 2}
              >
                {SubRow}
              </VirtualComponent>
            }
          </Grid>
          <Grid item xs={12}>
            <Button
              variant={"text"}
              className={"initial-text width-100p"}
              startIcon={<AddCircleOutline />}
              onClick={() => {
                const items = [...(option.subOptions || []), { key: "", label: "" }];
                updateSubOptions(i, items);
              }}
            >
              Add sub option
            </Button>
          </Grid>
        </>
      );
    };

    const onOpenTranslation = () => {
      const options = getValues("options");
      const radioRows = getValues("radioRows") || [];
      const content = getValues("content");
      const translations = getValues("translations");
      let res: KeyLabel[] = [{ key: "content", label: content }];
      if (type == QuestionTypeEnum.multipleRowColumnRadio) {
        res = [
          ...res,
          ...getOptionsKeyLabel(options.map(({ subOptions, ...rest }) => rest)),
          ...radioRows
            .filter(({ label }) => !/^[^a-zA-Z]*$/.test(label))
            .map(({ key, label }) => ({
              key: `${options[0].key}_rr_${key}`,
              label,
            })),
        ];
      } else {
        res = [...res, ...getOptionsKeyLabel(options)];
      }
      return {
        keyLabels: res,
        translations,
      };
    };

    return (
      <Box component="form" autoComplete="off">
        <Grid container spacing={2}>
          <Grid item xs={isModal ? 12 : 6}>
            <Paper elevation={isModal ? 0 : 3} className="padding-big">
              <Stack direction={"row"} justifyContent={"space-between"}>
                <Typography variant="h6" gutterBottom>
                  Question Content
                </Typography>
                <AddTranslationButton
                  onOpen={onOpenTranslation}
                  onSave={async (trans: any) => {
                    // @ts-ignore-next-line
                    setValue("translations", trans as any);
                    window.webApp.showMessage("success", "Translations saved. Click 'Save Changes' after editing other Question content.");
                  }}
                />
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormInputText
                    name="name"
                    control={control}
                    setError={setError}
                    label="Name"
                    required
                    rules={{ required: "Question name is required" }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormSelection
                    noEmpty
                    name="type"
                    control={control}
                    setError={setError}
                    label="Type"
                    options={enumToArraySelection(QuestionTypeEnum)}
                    required
                    rules={{ required: "Question type is required" }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormInputText name="content" control={control} setError={setError} label="Content" multiline rows={3} />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          {
            <Grid item xs={isModal ? 12 : 6}>
              <Paper elevation={isModal ? 0 : 3} className="padding-big">
                <Typography variant="h6" gutterBottom>
                  Question Answers
                </Typography>
                {isSelectionType(type) && !hasSubOptionsType(type) && (
                  <Stack sx={{ py: 2 }} flexDirection="row" alignItems="flex-start" justifyContent="space-between">
                    <div style={{ width: 200 }}>
                      <UploadCSVButton<BaseQuestionOption>
                        keys={["key", "label"]}
                        key={type}
                        onChange={(data) => {
                          const options = getValues("options");
                          const changeOptions = setValue as any;
                          changeOptions("options", options.concat(data));
                        }}
                        sample={`key,label\nsample_key,sample_label`}
                      />
                    </div>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        const changeOptions = setValue as any;
                        changeOptions("options", [{ label: "", key: "" }]);
                      }}
                    >
                      Clear All Option
                    </Button>
                  </Stack>
                )}
                {fields.map((option, i) => (
                  <div key={`${option.id}`}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Stack direction="row" justifyContent="space-between" spacing={2}>
                          <Typography variant="subtitle1">
                            {selection_type_label[type] || "Option"} {i + 1}
                          </Typography>
                          <Stack direction="row" spacing={2}>
                            {i + 1 < fields.length && (
                              <IconButton
                                color="primary"
                                onClick={() => {
                                  move(i, i + 1);
                                }}
                              >
                                <ArrowDownward />
                              </IconButton>
                            )}
                            {fields.length > 1 && (
                              <IconButton color="error" onClick={() => remove(i)}>
                                <Close />
                              </IconButton>
                            )}
                          </Stack>
                        </Stack>
                      </Grid>
                      <Grid item xs={12}>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <div>
                              <FormInputText
                                name={`options.${i}.key`}
                                control={control}
                                setError={setError}
                                label="Key"
                                required
                                rules={{
                                  required: "Key is required",
                                  pattern: {
                                    value: validateKeyMessageMap[type]?.regex || /^[\w-]+$/,
                                    message: validateKeyMessageMap[type]?.message || "Key can not use any special character or space",
                                  },
                                }}
                                helperText={
                                  helper_text[type] ||
                                  "Key is used for generating report, if it contains 'sentiment_' then it will be used for AI; if it contains 'filter_' then it will be used for filter, and must include - for seperate values; if it contains 'disable_future' then it will be used for disable future date; If it contains 'complain_' then it will be used for close loop; For date visit question the key must be Date_Time so that it can be used for generating report."
                                }
                              />
                            </div>
                          </Grid>
                          <Grid item xs={12}>
                            <FormInputText
                              name={`options.${i}.label`}
                              control={control}
                              setError={setError}
                              label="Label"
                              multiline
                              rows={3}
                              required
                              rules={{ required: "Label is required" }}
                            />
                          </Grid>
                          {type === QuestionTypeEnum.multipleDropdown && (
                            <>
                              <MultipleDropdownOption option={option} i={i} />
                              <Divider className="width-100p" sx={{ my: 2 }} />
                            </>
                          )}
                        </Grid>
                      </Grid>
                    </Grid>
                  </div>
                ))}
                {(isSelectionType(type) || fields.length === 0) && (
                  <Button
                    variant="contained"
                    className="initial-text margin-t-big width-100p"
                    startIcon={<AddCircleOutline />}
                    onClick={() => append({ label: "", key: "" })}
                  >
                    Add new {selection_type_label[type] || "Option"}
                  </Button>
                )}
                {<RadioRowsForm control={control} setError={setError} type={type} />}
              </Paper>
            </Grid>
          }
        </Grid>
      </Box>
    );
  }
);

const RadioRowsForm = (props: { control: Control<any, any>; setError: UseFormSetError<any>; type: QuestionTypeEnum }) => {
  const { control, setError, type } = props;
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "radioRows",
    rules: {
      required: "Please add some content",
      validate: (val) => {
        if (val && val.length < 1) {
          return `Please add at least one radio row group.`;
        }
        return true;
      },
    },
  });
  return type === QuestionTypeEnum.multipleRowColumnRadio ? (
    <>
      {fields.map((option, i) => (
        <div key={`${option.id}-${i}`}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Stack direction="row" justifyContent="space-between" spacing={2}>
                <Typography variant="subtitle1">Radio Column {i + 1}</Typography>
                <Stack direction="row" spacing={2}>
                  {i + 1 < fields.length && (
                    <IconButton
                      color="primary"
                      onClick={() => {
                        move(i, i + 1);
                      }}
                    >
                      <ArrowDownward />
                    </IconButton>
                  )}
                  {fields.length > 1 && (
                    <IconButton color="error" onClick={() => remove(i)}>
                      <Close />
                    </IconButton>
                  )}
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormInputText
                    name={`radioRows.${i}.key`}
                    control={control}
                    setError={setError}
                    label="Key"
                    required
                    rules={{
                      required: "Key is required",
                      pattern: {
                        value: /^[\w-]+$/,
                        message: "Key can not use any special character or space",
                      },
                    }}
                    helperText="Key is used for generating report"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormInputText
                    name={`radioRows.${i}.label`}
                    control={control}
                    setError={setError}
                    label="Label"
                    multiline
                    rows={3}
                    required
                    rules={{ required: "Label is required" }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </div>
      ))}
      <Button
        variant="contained"
        className="initial-text margin-t-big width-100p"
        startIcon={<AddCircleOutline />}
        onClick={() => append({ label: "", key: "" })}
      >
        Add new Radio Column
      </Button>
    </>
  ) : null;
};
